if (!customElements.get('lm-promo-tiers')) {
  customElements.define(
    'lm-promo-tiers',
    class LmPromoTiers extends HTMLElement {
      connectedCallback() {
        this.tiers = [...this.querySelectorAll('[data-tier]')].map((element) => ({
          min: parseInt(element.dataset.min, 10),
          discount: parseInt(element.dataset.discount, 10),
          element,
        }));

        this.messageEl = this.querySelector('[data-promo-message]');
        this.progressEl = this.querySelector('[data-promo-progress]');
        this.progressBar = this.querySelector('[data-promo-progress-bar]');

        const stringsNode = this.querySelector('[data-lm-promo-strings]');
        this.strings = stringsNode ? JSON.parse(stringsNode.textContent) : {};

        this.onCartUpdate = this.onCartUpdate.bind(this);
        this.onLinesUpdate = this.onLinesUpdate.bind(this);
        this.onCartIconChange = this.onCartIconChange.bind(this);

        this.initCartSync();
        this.refreshCount();
      }

      disconnectedCallback() {
        this.unsubscribeCart?.();
        this.cartIconObserver?.disconnect();
        document.removeEventListener('cart:lines-update', this.onLinesUpdate);
        clearTimeout(this.refreshTimer);
      }

      initCartSync() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        } else {
          window.addEventListener('load', () => {
            if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && !this.unsubscribeCart) {
              this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
            }
          }, { once: true });
        }

        document.addEventListener('cart:lines-update', this.onLinesUpdate);

        const cartIcon = document.getElementById('cart-icon-bubble');
        if (cartIcon && 'MutationObserver' in window) {
          this.cartIconObserver = new MutationObserver(this.onCartIconChange);
          this.cartIconObserver.observe(cartIcon, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      onCartUpdate() {
        this.scheduleRefresh();
      }

      onLinesUpdate(event) {
        const cart = event?.detail?.cart;
        const count = cart?.totalQuantity ?? cart?.itemCount;

        if (typeof count === 'number') {
          this.update(count);
          return;
        }

        this.scheduleRefresh();
      }

      onCartIconChange() {
        this.scheduleRefresh();
      }

      scheduleRefresh() {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = window.setTimeout(() => this.refreshCount(), 120);
      }

      refreshCount() {
        const fetchCart =
          typeof CartItems !== 'undefined' && typeof CartItems.fetchCartData === 'function'
            ? () => CartItems.fetchCartData()
            : () => {
                const cartUrl = window.routes?.cart_url || '/cart';
                return fetch(`${cartUrl}.json`).then((response) => response.json());
              };

        fetchCart()
          .then((cart) => {
            if (!cart) return;
            this.update(cart.item_count || 0);
          })
          .catch(() => {});
      }

      update(itemCount) {
        this.dataset.itemCount = String(itemCount);

        let activeTierIndex = -1;
        for (let index = this.tiers.length - 1; index >= 0; index -= 1) {
          if (itemCount >= this.tiers[index].min) {
            activeTierIndex = index;
            break;
          }
        }

        const nextTierIndex = activeTierIndex + 1;
        const maxItems = this.tiers[this.tiers.length - 1]?.min || 1;
        const progress = Math.min(itemCount / maxItems, 1) * 100;

        this.tiers.forEach((tier, index) => {
          const isActive = activeTierIndex >= 0 && index === activeTierIndex;

          tier.element.classList.toggle('is-active', isActive);
          tier.element.classList.remove('is-unlocked', 'is-next');
        });

        if (this.progressBar) {
          this.progressBar.style.width = `${progress}%`;
        }

        if (this.progressEl) {
          this.progressEl.setAttribute('aria-valuenow', String(Math.round(progress)));
        }

        if (!this.messageEl) return;

        if (nextTierIndex >= this.tiers.length) {
          const maxDiscount = this.tiers[this.tiers.length - 1].discount;
          this.messageEl.innerHTML = this.formatMessage(this.strings.unlocked, {
            count: 0,
            discount: maxDiscount,
          });
          return;
        }

        const nextTier = this.tiers[nextTierIndex];
        const itemsNeeded = Math.max(nextTier.min - itemCount, 0);
        const template = itemsNeeded === 1 ? this.strings.addMoreOne : this.strings.addMore;

        this.messageEl.innerHTML = this.formatMessage(template, {
          count: itemsNeeded,
          discount: nextTier.discount,
        });
      }

      formatMessage(template, { count, discount }) {
        if (!template) return '';

        return template
          .replace(/\{\{\s*count\s*\}\}/g, `<strong>${count}</strong>`)
          .replace(/\{\{\s*discount\s*\}\}/g, `<strong>${discount}%</strong>`);
      }
    }
  );
}
